import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { publicUrl } from '@/utils/publicUrl';
import { brandLogoUrl } from '@/assets/brand/urls';

export default function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const year = new Date().getFullYear();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d4pbbnbm5ik2goc1es0g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const solutionLinks = t('footer.solutions', { returnObjects: true }) as string[];

  return (
    <footer className="bg-slate-950 border-t border-cyan-500/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        {/* Top Section */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center mb-6">
              <img
                src={brandLogoUrl()}
                alt={t('footer.brand')}
                className="h-8 w-auto max-w-[min(16rem,48vw)] object-contain object-left"
              />
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-3">
              {t('footer.tagline')}
            </p>
            <p className="mb-6">
              <a
                href={publicUrl(t('footer.localFileUrl'))}
                download
                className="text-sm text-cyan-400/90 hover:text-cyan-400 underline-offset-2 hover:underline transition-colors"
              >
                {t('footer.localFileLabel')}
              </a>
            </p>
            <div className="flex gap-3">
              <a href="#" className="w-10 h-10 flex items-center justify-center rounded-lg bg-slate-900 border border-cyan-500/20 text-gray-400 hover:text-cyan-400 hover:border-cyan-400 transition-all cursor-pointer">
                <i className="ri-linkedin-fill text-lg"></i>
              </a>
              <a href="#" className="w-10 h-10 flex items-center justify-center rounded-lg bg-slate-900 border border-cyan-500/20 text-gray-400 hover:text-cyan-400 hover:border-cyan-400 transition-all cursor-pointer">
                <i className="ri-twitter-x-fill text-lg"></i>
              </a>
              <a href="#" className="w-10 h-10 flex items-center justify-center rounded-lg bg-slate-900 border border-cyan-500/20 text-gray-400 hover:text-cyan-400 hover:border-cyan-400 transition-all cursor-pointer">
                <i className="ri-github-fill text-lg"></i>
              </a>
            </div>
          </div>

          {/* Solutions */}
          <div>
            <h6 className="text-white font-bold mb-6">{t('footer.colSolutions')}</h6>
            <ul className="space-y-3">
              {solutionLinks.map((label) => (
                <li key={label}>
                  <a href="#" className="text-sm text-gray-400 hover:text-cyan-400 transition-colors cursor-pointer">
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h6 className="text-white font-bold mb-6">{t('footer.colCompany')}</h6>
            <ul className="space-y-3">
              <li><a href="#about" className="text-sm text-gray-400 hover:text-cyan-400 transition-colors cursor-pointer">{t('footer.companyAbout')}</a></li>
              <li><a href="#" className="text-sm text-gray-400 hover:text-cyan-400 transition-colors cursor-pointer">{t('footer.companyCareers')}</a></li>
              <li><a href="#" className="text-sm text-gray-400 hover:text-cyan-400 transition-colors cursor-pointer">{t('footer.companyCase')}</a></li>
              <li><a href="#contact" className="text-sm text-gray-400 hover:text-cyan-400 transition-colors cursor-pointer">{t('footer.companyContact')}</a></li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h6 className="text-white font-bold mb-6">{t('footer.colNewsletter')}</h6>
            <p className="text-sm text-gray-400 mb-4">
              {t('footer.newsletterLead')}
            </p>
            <form id="newsletter-form" data-readdy-form onSubmit={handleSubmit} className="space-y-3">
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('footer.emailPlaceholder')}
                required
                disabled={isSubmitting}
                className="w-full px-4 py-3 bg-slate-900 border border-cyan-500/20 text-white text-sm rounded-lg focus:outline-none focus:border-cyan-400 transition-colors"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-6 py-3 bg-gradient-to-r from-cyan-500 to-teal-500 text-white text-sm font-semibold rounded-lg hover:shadow-lg hover:shadow-cyan-500/50 transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                {isSubmitting ? t('footer.subscribing') : t('footer.subscribe')}
                <i className="ri-arrow-right-line"></i>
              </button>
            </form>
            {submitStatus === 'success' && (
              <p className="text-green-400 text-xs mt-2">{t('footer.subscribeSuccess')}</p>
            )}
            {submitStatus === 'error' && (
              <p className="text-red-400 text-xs mt-2">{t('footer.subscribeError')}</p>
            )}
          </div>
        </div>

        {/* Bottom Section */}
        <div className="pt-8 border-t border-cyan-500/10">
          <p className="text-sm text-gray-500 text-center md:text-left">
            {t('footer.copyright', { year })}
          </p>
        </div>
      </div>
    </footer>
  );
}
